<template>
  <admin-layout :title="$t('edit_x', { x: $t('Customer') })">
    <tec-form-section @submitted="update">
      <template #title>
        <div class="flex items-center">
          <Link class="text-indigo-400 hover:text-indigo-600" :href="route('customers')">{{ $t('Customers') }}</Link>
          <span class="text-indigo-400 font-medium mx-2">/</span>
          <img v-if="edit.photo" class="block w-6 h-6 rounded-full mr-2" :src="edit.photo" />
          {{ edit.name }}
        </div>
      </template>

      <template #description>{{ $t('Update the record by modifying the details in the form below') }}</template>

      <template #form>
        <div class="col-span-full">
          <trashed-message v-if="edit.deleted_at" class="mb-6" @restore="restore">
            {{ $t('This record has been deleted.') }}
          </trashed-message>
        </div>

        <div class="col-span-full flex items-start flex-wrap -mr-4 md:-mr-6 md:rounded-md">
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input
                :label="$t('Name')"
                v-model="form.name"
                :error="$page.props.errors.name ? $page.props.errors.name.replace('{attribute}', 'name') : null"
              />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input
                :label="$t('Company')"
                v-model="form.company"
                :error="$page.props.errors.company ? $page.props.errors.company.replace('{attribute}', 'company') : null"
              />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input
                :label="$t('Phone')"
                v-model="form.phone"
                :error="$page.props.errors.phone ? $page.props.errors.phone.replace('{attribute}', 'phone').split('when')[0] : null"
              />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input
                type="email"
                :label="$t('Email')"
                v-model="form.email"
                :error="$page.props.errors.email ? $page.props.errors.email.replace('{attribute}', 'email') : null"
              />
            </div>
          </div>

          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input
                type="date"
                v-model="form.dob"
                :label="$t('Date of Birth')"
                :error="$page.props.errors.dob ? $page.props.errors.dob.replace('{attribute}', 'date of birth') : null"
              />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input
                :label="$t('GST')"
                v-model="form.gst"
                :error="$page.props.errors.gst ? $page.props.errors.gst.replace('{attribute}', 'GST') : null"
              />
            </div>
          </div>
          <div class="w-full">
            <div class="mr-6 mb-6">
              <text-input
                :label="$t('Address')"
                v-model="form.address"
                :error="$page.props.errors.address ? $page.props.errors.address.replace('{attribute}', 'address') : null"
              />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input
                :label="$t('City')"
                v-model="form.city"
                :error="$page.props.errors.city ? $page.props.errors.city.replace('{attribute}', 'city') : null"
              />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input
                :label="$t('State')"
                v-model="form.state"
                :error="$page.props.errors.state ? $page.props.errors.state.replace('{attribute}', 'state') : null"
              />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input
                :label="$t('Postal Code')"
                v-model="form.postal_code"
                :error="$page.props.errors.postal_code ? $page.props.errors.postal_code.replace('{attribute}', 'postal code') : null"
              />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input
                :label="$t('Country')"
                v-model="form.country"
                :error="$page.props.errors.country ? $page.props.errors.country.replace('{attribute}', 'email') : null"
              />
            </div>
          </div>
        </div>
      </template>

      <template #actions>
        <div class="w-full flex items-center justify-between">
          <button
            type="button"
            @click="destroy"
            v-if="!edit.deleted_at"
            class="text-red-600 px-4 py-2 rounded border-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-300"
          >
            {{ $t('delete_x', { x: $t('Customer') }) }}
          </button>
          <button
            v-else
            type="button"
            @click="deletePermanently"
            class="text-red-600 px-4 py-2 rounded border-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-300"
          >
            {{ $t('delete_x', { x: $t('Permanently') }) }}
          </button>
          <div class="flex items-center">
            <tec-action-message :on="form.recentlySuccessful" class="mr-3">{{ $t('Saved.') }}</tec-action-message>
            <loading-button type="submit" :loading="form.processing" :disabled="form.processing">{{ $t('Save') }}</loading-button>
          </div>
        </div>
      </template>
    </tec-form-section>

    <!-- Delete User Confirmation Modal -->
    <Dialog
      max-width="md"
      :show="permanent"
      action-type="delete"
      :close="closePermanentModal"
      :title="$t('Delete Customer?')"
      :action="deleteCustomerPermanently"
      :action-text="$t('Delete Permanently')"
      :content="`<p class='mb-2'>${$t('Are you sure you want to delete the record permanently?')}</p>
        <p>${$t('Once deleted, all of its resources and data will be permanently deleted.')}</p>`"
    />

    <!-- Delete Account Confirmation Modal -->
    <Dialog
      :show="confirm"
      :close="closeModal"
      :action="deleteCustomer"
      action-type="delete"
      :title="$t('Delete Customer?')"
      :action-text="$t('Delete Customer')"
      :content="$t('Are you sure you want to delete the record?')"
    />

    <!-- Restore Account Confirmation Modal -->
    <Dialog
      :show="restoreConf"
      :action="restoreCustomer"
      :title="$t('Restore Customer!')"
      :close="closeRestoreModal"
      :action-text="$t('Restore Customer')"
      :content="$t('Are you sure you want to restore the record?')"
    />
  </admin-layout>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3';

import Dialog from '@/Shared/Dialog';
import TextInput from '@/Shared/TextInput';
import FileInput from '@/Shared/FileInput';
import SelectInput from '@/Shared/SelectInput';
import AdminLayout from '@/Layouts/AdminLayout';
import LoadingButton from '@/Shared/LoadingButton';
import TecDialogModal from '@/Jetstream/DialogModal';
import TrashedMessage from '@/Shared/TrashedMessage';
import TecFormSection from '@/Jetstream/FormSection';
import TecDangerButton from '@/Jetstream/DangerButton';
import TecSectionTitle from '@/Jetstream/SectionTitle';
import TecActionMessage from '@/Jetstream/ActionMessage';
import TecSecondaryButton from '@/Jetstream/SecondaryButton';

export default {
  props: ['edit'],

  components: {
    Link,
    Dialog,
    FileInput,
    TextInput,
    AdminLayout,
    SelectInput,
    LoadingButton,
    TecDialogModal,
    TecFormSection,
    TecDangerButton,
    TrashedMessage,
    TecSectionTitle,
    TecActionMessage,
    TecSecondaryButton,
  },

  data() {
    return {
      confirm: false,
      permanent: false,
      restoreConf: false,
      form: this.$inertia.form({
        _method: 'put',
        name: this.edit.name,
        email: this.edit.email,
        phone: this.edit.phone,
        company: this.edit.company,
        gst: this.edit.gst,
        vat: this.edit.vat,
        address: this.edit.address,
        city: this.edit.city,
        state: this.edit.state,
        postal_code: this.edit.postal_code,
        country: this.edit.country,
        dob: this.edit.dob,
      }),
    };
  },

  methods: {
    update() {
      this.form.post(this.route('customers.update', this.edit.id), {
        preserveScroll: true,
        onSuccess: () => this.form.reset('password', 'photo'),
      });
    },
    destroy() {
      this.confirm = true;
    },
    deleteCustomer() {
      this.form.delete(route('customers.destroy', this.edit.id), {
        onSuccess: () => this.closeModal(),
      });
    },
    closeModal() {
      this.confirm = false;
    },
    restore() {
      this.restoreConf = true;
    },
    restoreCustomer() {
      this.$inertia.put(this.route('customers.restore', this.edit.id), {
        onSuccess: () => (this.restoreConf = false),
      });
    },
    closeRestoreModal() {
      this.restoreConf = false;
    },
    deletePermanently() {
      this.permanent = true;
    },
    deleteCustomerPermanently() {
      this.form.delete(route('customers.destroy.permanently', this.edit.id), {
        onSuccess: () => this.closePermanentModal(),
      });
    },
    closePermanentModal() {
      this.permanent = false;
    },
  },
};
</script>
